import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatAmount } from '../lib/format';
import type { Currency, Reports as ReportsData } from '../lib/types';

interface Props {
  onBack: () => void;
}

export default function Reports({ onBack }: Props) {
  const [data, setData] = useState<ReportsData | null>(null);
  const [currency, setCurrency] = useState<Currency>('SYP');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.reports()
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : 'failed to load'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Reports</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <div className="segmented report-toggle">
        <button className={currency === 'SYP' ? 'active' : ''} onClick={() => setCurrency('SYP')}>SYP</button>
        <button className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>USD</button>
      </div>

      {loading && <SkeletonReports />}

      {data && <>
        <CurrentBalance data={data} currency={currency} />
        <MonthOverview data={data} currency={currency} />
        <InsightsCard data={data} currency={currency} />
        <CategoryBreakdown data={data} currency={currency} />
        <DailyChart data={data} currency={currency} />
        <MonthlyTrend data={data} currency={currency} />
      </>}
    </>
  );
}

function InsightsCard({ data, currency }: { data: ReportsData; currency: Currency }) {
  const items = data.insights[currency];
  if (!items || items.length === 0) return null;
  return (
    <div className="card">
      <SectionTitle>💡 Insights</SectionTitle>
      {items.map((t, i) => (
        <div key={i} className="insight-row">{t}</div>
      ))}
    </div>
  );
}

function MonthlyTrend({ data, currency }: { data: ReportsData; currency: Currency }) {
  const pts = data.monthly_trend;
  const max = Math.max(...pts.map((p) => p[currency]), 1);
  const lastIdx = pts.length - 1;
  return (
    <div className="card">
      <SectionTitle>📈 Last 6 months</SectionTitle>
      <div className="trend-chart">
        {pts.map((p, i) => {
          const v = p[currency];
          const h = (v / max) * 100;
          const [y, m] = p.month.split('-');
          const label = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'short' });
          return (
            <div className="trend-col" key={p.month} title={`${label}: ${formatAmount(v, currency)}`}>
              <div className="trend-bar-area">
                <div
                  className={`trend-bar${i === lastIdx ? ' current' : ''}`}
                  style={{ height: `${v === 0 ? 2 : Math.max(h, 4)}%` }}
                />
              </div>
              <div className="trend-label">{label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SectionTitle({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="report-section-title">
      <span>{children}</span>
      {right != null && <span className="report-section-right">{right}</span>}
    </div>
  );
}

function CurrentBalance({ data, currency }: { data: ReportsData; currency: Currency }) {
  return (
    <div className="card hero">
      <div className="hero-label">Current balance · {currency}</div>
      <div className="hero-amount">{formatAmount(data.balances[currency], currency)}</div>
      <div className="muted small">Running total, including your starting balance</div>
    </div>
  );
}

function MonthOverview({ data, currency }: { data: ReportsData; currency: Currency }) {
  const income  = data.this_month.income[currency];
  const expense = data.this_month.expense[currency];
  const net     = data.this_month.net[currency];
  const lastInc = data.last_month.income[currency];
  const lastExp = data.last_month.expense[currency];
  const { days_elapsed, days_in_month } = data.this_month;

  // Use days actually tracked this month for the pace divisor, not host calendar
  // day-of-month — otherwise a first-month user gets a wildly understated pace.
  const tracked = Math.min(data.days_active, days_elapsed);
  const pace = tracked > 0 ? (expense / tracked) * days_in_month : 0;
  const monthPct = Math.min(100, (days_elapsed / days_in_month) * 100);
  const adj = data.adjustments_this_month?.[currency] ?? 0;

  return (
    <div className="card">
      <SectionTitle right={`day ${days_elapsed}/${days_in_month}`}>📅 This month</SectionTitle>

      <div className="month-row">
        <div className="month-cell">
          <div className="stat-label">Income</div>
          <div className="stat-value amount-pos">{formatAmount(income, currency)}</div>
          <Delta current={income} prev={lastInc} kind="income" currency={currency} />
        </div>
        <div className="month-cell">
          <div className="stat-label">Expense</div>
          <div className="stat-value amount-neg">{formatAmount(expense, currency)}</div>
          <Delta current={expense} prev={lastExp} kind="expense" currency={currency} />
        </div>
      </div>

      <div className="month-net">
        <span className="muted small">Net change this month</span>
        <span className={`month-net-value ${net >= 0 ? 'amount-pos' : 'amount-neg'}`}>
          {formatAmount(net, currency)}
        </span>
      </div>

      {expense > 0 && (
        <div className="month-progress-wrap">
          <div className="month-progress">
            <div className="month-progress-fill" style={{ width: `${monthPct}%` }} />
          </div>
          <div className="muted small" style={{ marginTop: 6, textAlign: 'center' }}>
            At this pace, ~<strong>{formatAmount(pace, currency)}</strong> spent by month end
          </div>
        </div>
      )}

      {Math.abs(adj) > 0.005 && (
        <div className="muted small" style={{ marginTop: 12, textAlign: 'center' }}>
          Untracked balance adjustments this month:{' '}
          <span className={adj < 0 ? 'amount-neg' : 'amount-pos'} style={{ fontWeight: 700 }}>
            {adj < 0 ? '−' : '+'}{formatAmount(Math.abs(adj), currency)}
          </span>
        </div>
      )}
    </div>
  );
}

function Delta({ current, prev, kind, currency }: { current: number; prev: number; kind: 'income' | 'expense'; currency: Currency }) {
  if (prev === 0 && current === 0) return <div className="stat-delta neutral">—</div>;
  const diff = current - prev;
  const pct = prev > 0 ? Math.round((diff / prev) * 100) : (current > 0 ? 100 : 0);
  const isBad = kind === 'expense' ? diff > 0 : diff < 0;
  const cls = diff === 0 ? 'neutral' : (isBad ? 'up' : 'down');
  const arrow = diff === 0 ? '·' : (diff > 0 ? '↑' : '↓');
  if (prev === 0) {
    return <div className={`stat-delta ${cls}`}>{arrow} new vs. last month</div>;
  }
  return <div className={`stat-delta ${cls}`}>{arrow} {Math.abs(pct)}% vs. last month</div>;
}

function CategoryBreakdown({ data, currency }: { data: ReportsData; currency: Currency }) {
  const cats = data.by_category[currency];
  return (
    <div className="card">
      <SectionTitle>🗂 Spending by category</SectionTitle>
      {cats.length === 0 ? (
        <div className="empty" style={{ padding: '16px 0' }}>No expenses yet this month.</div>
      ) : cats.map((c, i) => (
        <div className="cat-row" key={c.category}>
          <div className="cat-head">
            <span className="cat-name">{c.category}</span>
            <span>
              <span className="muted small" style={{ marginRight: 8 }}>{c.percent.toFixed(0)}%</span>
              <strong>{formatAmount(c.amount, currency)}</strong>
            </span>
          </div>
          <div className="cat-bar">
            <div className={`cat-bar-fill cat-c${i % 6}`} style={{ width: `${Math.max(c.percent, 2)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DailyChart({ data, currency }: { data: ReportsData; currency: Currency }) {
  const points = data.daily_spend_last_30;
  const max = Math.max(...points.map((p) => p[currency]), 1);
  const total = points.reduce((s, p) => s + p[currency], 0);
  const divisor = Math.min(data.days_active, points.length);
  const avg = total / Math.max(divisor, 1);
  const firstMonth = data.days_active < points.length;
  const peakValue = Math.max(...points.map((p) => p[currency]));
  const lastIdx = points.length - 1;
  const avgPct = Math.min(100, (avg / max) * 100);

  return (
    <div className="card">
      <SectionTitle>📊 Daily spending — last 30 days</SectionTitle>
      <div className="row" style={{ padding: 0, marginTop: 2, borderTop: 'none' }}>
        <div className="small">Total <strong>{formatAmount(total, currency)}</strong></div>
        <div className="small muted">Avg/day {formatAmount(avg, currency)}</div>
      </div>
      {firstMonth && (
        <div className="muted small" style={{ marginTop: 2 }}>
          Average over your {data.days_active} day{data.days_active === 1 ? '' : 's'} using the app
        </div>
      )}

      <div className="daily-chart">
        {avg > 0 && <div className="chart-avg-line" style={{ bottom: `${avgPct}%` }} />}
        {points.map((p, i) => {
          const v = p[currency];
          const h = (v / max) * 100;
          const isPeak = v > 0 && v === peakValue;
          const isToday = i === lastIdx;
          const cls = v === 0 ? 'day-bar empty' : `day-bar${isPeak ? ' peak' : ''}${isToday ? ' today' : ''}`;
          return (
            <div key={p.date} className="day" title={`${p.date}: ${formatAmount(v, currency)}`}>
              <div className={cls} style={{ height: `${v === 0 ? 4 : Math.max(h, 6)}%` }} />
            </div>
          );
        })}
      </div>
      <div className="chart-axis">
        <span>{shortDate(points[0]?.date)}</span>
        <span className="muted">┈ avg</span>
        <span>{shortDate(points[lastIdx]?.date)}</span>
      </div>
    </div>
  );
}

function SkeletonReports() {
  return (
    <>
      <div className="card hero"><div className="skeleton" style={{ height: 44, width: '60%' }} /></div>
      <div className="card">
        <div className="skeleton" style={{ height: 14, width: '40%', marginBottom: 14 }} />
        <div className="skeleton" style={{ height: 50 }} />
      </div>
      <div className="card">
        <div className="skeleton" style={{ height: 14, width: '50%', marginBottom: 14 }} />
        <div className="skeleton" style={{ height: 80 }} />
      </div>
    </>
  );
}

function shortDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
